import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ExternalLink, LogOut, Package, FolderOpen, MailQuestion, BookOpen } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import ProductsTab from "./ProductsTab";
import EnquiriesTab from "./EnquiriesTab";
import BlogTab from "./BlogTab";
import PhotosTab from "./PhotosTab";

export default function AdminDashboardPage() {
  const { user, checking, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [statsKey, setStatsKey] = useState(0);

  useEffect(() => {
    if (user) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user, statsKey]);

  if (checking) {
    return (
      <div className="grain-overlay flex min-h-screen items-center justify-center bg-[#111110]">
        <p className="font-hand text-2xl text-[#C27803]">lighting the lamps...</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const refreshStats = () => setStatsKey((k) => k + 1);

  const cards = [
    { label: "Products on Shelves", value: stats?.products, icon: Package, testid: "stat-products" },
    { label: "Active Categories", value: stats?.categories, icon: FolderOpen, testid: "stat-categories" },
    { label: "Open Enquiries", value: stats?.open_enquiries, icon: MailQuestion, testid: "stat-enquiries" },
    { label: "Journal Stories", value: stats?.blog_posts, icon: BookOpen, testid: "stat-posts" },
  ];

  return (
    <div className="min-h-screen bg-[#F4EFE6]" data-testid="admin-dashboard">
      <header className="grain-overlay bg-[#111110]">
        <div className="crow-container flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-horizontal.jpeg" alt="The Olde Crow Boutique" className="h-12 w-auto rounded-md border border-[#3A3632] bg-[#F4EFE6] px-1.5 py-0.5" />
            <div className="leading-tight">
              <p className="font-serif text-base font-bold text-[#FDFBF7]">The Shop Office</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A7E70]">The Olde Crow Boutique</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site-link" className="hidden items-center gap-1.5 rounded-md border border-[#3A3632] px-3 py-2 text-xs font-medium text-[#E8DFC8] transition-colors hover:border-[#C27803] hover:text-[#C27803] sm:flex">
              <ExternalLink className="h-3.5 w-3.5" /> View Site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-1.5 rounded-md bg-[#8C251C] px-3 py-2 text-xs font-semibold text-[#FDFBF7] transition-colors hover:bg-[#6B1912]"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="crow-container py-10">
        <p className="eyebrow-label">Welcome back, {user.name || "Shopkeeper"}</p>
        <h1 className="mt-2 font-serif text-3xl font-semibold tracking-tight text-[#292725]">Tend the shop</h1>

        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="admin-stats-grid">
          {cards.map((c) => (
            <div key={c.label} className="rounded-md border border-[#D8CDBC] bg-[#FDFBF7] p-5 shadow-sm">
              <c.icon className="h-5 w-5 text-[#C27803]" />
              <p className="mt-3 font-serif text-3xl font-bold text-[#292725]" data-testid={c.testid}>
                {c.value ?? "—"}
              </p>
              <p className="mt-1 text-xs font-medium uppercase tracking-wider text-[#8A7E70]">{c.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="products" className="mt-10" onValueChange={refreshStats}>
          <TabsList className="border border-[#D8CDBC] bg-[#FDFBF7]">
            <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="blog" data-testid="admin-tab-blog">Journal Stories</TabsTrigger>
            <TabsTrigger value="photos" data-testid="admin-tab-photos">Photos</TabsTrigger>
          </TabsList>
          <TabsContent value="products" className="mt-6">
            <ProductsTab onChanged={refreshStats} />
          </TabsContent>
          <TabsContent value="enquiries" className="mt-6">
            <EnquiriesTab onChanged={refreshStats} />
          </TabsContent>
          <TabsContent value="blog" className="mt-6">
            <BlogTab onChanged={refreshStats} />
          </TabsContent>
          <TabsContent value="photos" className="mt-6">
            <PhotosTab onChanged={refreshStats} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
